import React, { forwardRef, useEffect, useState } from "react";
import { StyleSheet } from "react-native";
import { Modal } from "~/components/Modal";
import { Spacer } from "~/components/Spacer";
import type { TouchableOpacityProps } from "~/components/TouchableOpacity";
import { View } from "~/components/View";
import { styleConstantsV2 as C } from "~/style/styleConstantsV2";
import { isPromise } from "~/utils/isPromise";
import { shadow } from "~/utils/shadow";
import { Pressable } from "./Pressable";
import { SpinnerV2 } from "./SpinnerV2";
import { BrandText } from "./Text/BrandText";
import type { PressableState } from "./Pressable";
import type { ReactNode } from "react";
import type { TextStyle } from "react-native";

export interface ButtonProps extends TouchableOpacityProps {
  title?: string;
  isLoading?: boolean | undefined;
  small?: boolean;
  medium?: boolean;

  primary?: boolean;
  primaryReversed?: boolean;
  secondaryReversed?: boolean;
  secondary?: boolean;
  tertiary?: boolean;
  approval?: boolean;
  destructive?: boolean;

  hasShadow?: boolean;

  children?: ReactNode;
  onPress?:
    | TouchableOpacityProps["onPress"]
    | ((...args: any[]) => Promise<any>);
  blockUi?: boolean;
  textStyle?: TextStyle;
}
export type Button = typeof ButtonV2;

export const ButtonV2 = forwardRef<Pressable, ButtonProps>(
  (
    {
      title,

      isLoading: isLoadingProp,

      small = false,
      medium = false,

      primary = true,
      primaryReversed = false,
      secondaryReversed = false,
      secondary = false,
      tertiary = false,
      approval = false,
      destructive = false,

      hasShadow = false,

      style: inheritedStyle,
      disabled,
      children,
      onPress,
      blockUi = true,

      textStyle: textStylePassThrough,
      ...props
    },
    ref,
  ) => {
    const isMounted = React.useRef<boolean>(true);
    useEffect(() => {
      return () => {
        isMounted.current = false;
      };
    }, []);

    const shouldRenderTitle = typeof title === "string";

    const resolveBackgroundColor = ({
      pressed = false,
      hovered = false,
    }: {
      pressed: boolean;
      hovered?: boolean;
    }) => {
      if (disabled) {
        return C.colorGrey20;
      }

      if (primaryReversed) {
        if (pressed) return C.colorGrey20;
        if (hovered) return C.colorGrey20;

        return C.colorWhite;
      }
      if (secondary) {
        if (pressed) return C.colorPrimaryBg;
        if (hovered) return C.colorPrimaryBg;

        return C.colorWhite;
      }

      if (secondaryReversed) {
        if (pressed) return C.colorGrey10;
        if (hovered) return C.colorGrey10;

        return "transparent";
      }

      if (tertiary) {
        if (pressed) return C.colorWhite;
        if (hovered) return C.colorTertiaryBg;

        return C.colorWhite;
      }
      if (approval) return C.colorSuccess;
      if (destructive) return "transparent";

      if (primary) {
        if (pressed) return C.colorPrimaryHoverDark;
        if (hovered) return C.colorPrimaryHoverDark;

        return C.colorPrimary;
      }

      return C.colorPrimary;
    };

    const resolveHeight = () => {
      if (small) return 34;
      if (medium) return 42;

      return 50;
    };

    const resolveTextStyle = ({
      pressed = false,
      hovered = false,
    }: {
      pressed: boolean;
      hovered?: boolean;
    }) => {
      const style: TextStyle = {};
      style.fontSize = C.fontSizeDesktopH4;
      style.color = C.colorWhite;
      style.fontFamily = "DMSans-SemiBold";

      if (primary) {
        style.color = C.colorWhite;
      }
      if (primaryReversed) {
        style.color = C.colorPrimary;
      }
      if (secondary) {
        style.color = C.colorPrimary;
      }
      if (secondaryReversed) {
        if (pressed) {
          style.color = C.colorBlack;
        } else if (hovered) {
          style.color = C.colorBlack;
        } else {
          style.color = C.colorWhite;
        }
      }

      if (tertiary) {
        style.color = C.colorBlack;
      }

      if (approval) {
        style.color = C.colorWhite;
      }
      if (destructive) {
        style.color = C.colorError;
      }

      if (disabled) {
        style.color = C.colorGrey50;
      }

      return style;
    };

    const resolveBorderColor = () => {
      if (secondary) {
        return C.colorPrimary;
      }
      if (secondaryReversed) {
        return C.colorWhite;
      }
      if (destructive) {
        return C.colorError;
      }

      return "transparent";
    };

    const resolveShadow = () => {
      return 0;
    };

    const resolveSpinnerColor = () => {
      if (primary) return C.colorSecondary;
      if (primaryReversed) return C.colorSecondary;
      if (secondary) return C.colorSecondary;

      return C.colorWhite;
    };

    const [isLoadingState, setIsLoading] = useState(false);
    const isLoading = isLoadingProp || isLoadingState;

    const borderRadius = 8; // shared between the button and the spinner overlay
    const style: TouchableOpacityProps["style"] = {
      flexDirection: "row",
      justifyContent: "center", // ideja kod dodavanja ikona -> children != null ? "flex-start" : "center"
      alignItems: "center",
      backgroundColor: resolveBackgroundColor({
        hovered: false,
        pressed: false,
      }),
      borderColor: resolveBorderColor(),
      borderWidth: 2,
      height: resolveHeight(),
      paddingHorizontal: C.spacing16,
      borderRadius,
      ...shadow(resolveShadow()),
      opacity: isLoading ? 0.5 : 1,
    };

    return (
      <>
        <Pressable
          ref={ref as any}
          style={(state: Omit<PressableState, "focused">) => {
            const backgroundColor = resolveBackgroundColor(state);

            const borderColor = resolveBorderColor();

            return [
              style,
              inheritedStyle,
              hasShadow ? { ...shadow(2) } : undefined,
              {
                // opacity: disabled ? 0.4 : 1,
                backgroundColor,
                borderColor,
              },
            ];
          }}
          onPress={(event) => {
            if (typeof onPress === "function") {
              const maybePromise = onPress(event);

              if (isPromise(maybePromise)) {
                setIsLoading(true);

                maybePromise.finally(() => {
                  if (isMounted.current) {
                    setIsLoading(false);
                  }
                });
              }
            }
          }}
          disabled={isLoading || disabled}
          {...props}
        >
          {/* @ts-ignore  */}
          {({ pressed, hovered }: Omit<PressableState, "focused">) => {
            const textStyle = resolveTextStyle({
              hovered: hovered,
              pressed: pressed,
            });

            return (
              <>
                <>
                  {children}
                  {Boolean(children && shouldRenderTitle) && <Spacer small />}
                  {shouldRenderTitle && (
                    <BrandText
                      numberOfLines={1}
                      weightRegular
                      sizeH4
                      style={{ ...textStyle, ...textStylePassThrough }}
                    >
                      {title}
                    </BrandText>
                  )}
                </>

                {isLoading && (
                  <View
                    centerContent
                    style={{ ...StyleSheet.absoluteFillObject, borderRadius }}
                  >
                    <SpinnerV2 size="small" color={resolveSpinnerColor()} />
                  </View>
                )}
              </>
            );
          }}
        </Pressable>

        {Boolean(blockUi && isLoading) && (
          <Modal blockHardwareBackButton>
            <Pressable>
              <View flex />
            </Pressable>
          </Modal>
        )}
      </>
    );
  },
);
